import { useEffect, useMemo, useState } from 'react';
import SaleDurationField from './SaleDurationField';
import SaleAmountFields from './SaleAmountFields';
import GymIdMemberSearch from '../../../components/receptionist/GymIdMemberSearch';
import { durationOptionsForSaleSource, formatDurationLabel, durationFromSalePackage, cleanSalePackageName, syncSalePeriodFields, saleEndDate } from './saleDurationUtils';
import {
  defaultPaymentSplits,
  parsePaymentSplitsFromRow,
  sumPaymentSplits,
} from '../../../utils/splitPaymentUtils';
import {
  getSaleRowPeriod,
  saleRowPackageName,
  saleRowDuration,
  saleRowTrainerName,
  isTrainerSaleSource,
  stripSaleTypePrefix,
} from './salePeriodUtils';
import { saleRowTotal } from './salePaymentUtils';
import { fetchMembersPage, updatePaymentSale } from '../../../services/receptionist/receptionistApi';
import { notifyPaymentsUpdated } from '../paymentStore';
import { usePanelToast } from '../../../context/PanelToastContext';
import { formatCurrency } from '../receptionistData';
import { dedupeMembers } from '../../../utils/dedupeList';
import { getGymMemberId } from '../../admin/members/memberData';

function durationOptionsForSource(source) {
  return durationOptionsForSaleSource(source);
}

function isMembershipLikeSource(source) {
  const s = String(source || '').trim().toLowerCase();
  return (
    s === 'subscription'
    || s === 'membership'
    || s === 'new_membership'
    || s === 'subscriptions'
    || s === 'renewal_sale'
    || s === 'renewal'
  );
}

function matchDurationOption(raw, options) {
  const text = String(raw || '').trim();
  if (!text || text === '—') return '';
  if (options.some((o) => String(o.value) === text)) return text;
  const byLabel = options.find(
    (o) => String(o.label).toLowerCase() === text.toLowerCase(),
  );
  return byLabel?.value || '';
}

function resolveDurationKey(row, options, period = null) {
  const fromStored = matchDurationOption(row?.duration, options);
  if (fromStored) return fromStored;

  const fromPeriod = matchDurationOption(period?.durationLabel, options);
  if (fromPeriod) return fromPeriod;

  const fromDisplay = matchDurationOption(saleRowDuration(row), options);
  if (fromDisplay) return fromDisplay;

  const fromPkg = matchDurationOption(
    durationFromSalePackage(row?.package || ''),
    options,
  );
  if (fromPkg) return fromPkg;

  return options[0]?.value || '1m';
}

function userNoteOnly(paymentNote) {
  return String(paymentNote || '')
    .split(' · ')
    .map((p) => p.trim())
    .filter((p) => p && !/^(Duration:|Renewed|Complimentary renewal|Phone:)/i.test(p))
    .join(' · ');
}

/** Prefill like Membership edit: use saved start/end when present, else derive. */
function buildInitialForm(row, itemLabelFallback) {
  const options = durationOptionsForSource(row?.source);
  const period = getSaleRowPeriod(row);
  const duration = resolveDurationKey(row, options, period);
  const saleDate = String(row?.date || row?.paidAt || '').slice(0, 10);
  const startDate = String(period.startDate || row?.startDate || '').slice(0, 10)
    || saleDate;
  const storedEnd = String(period.endDate || row?.endDate || '').slice(0, 10);
  const endDate = storedEnd
    || (startDate ? saleEndDate(startDate, duration) : '');
  const splits = parsePaymentSplitsFromRow(row);
  const paymentSplits = splits.length
    ? defaultPaymentSplits().map((blank) => {
      const hit = splits.find((s) => s.method === blank.method);
      return hit ? { method: blank.method, amount: String(hit.amount) } : blank;
    })
    : defaultPaymentSplits();

  const itemValue = isTrainerSaleSource(row?.source)
    ? saleRowTrainerName(row, '')
    : saleRowPackageName(row, itemLabelFallback || '—');

  const serial = String(row?.gymId || row?.serialNo || '').trim();
  const membershipLike = isMembershipLikeSource(row?.source);
  const packageFee = Number(row?.packagePrice ?? row?.packageFee);
  const registrationFee = Number(row?.registrationFee) || 0;
  const total = saleRowTotal(row);

  return {
    gymId: !serial || serial === '—' ? '' : serial,
    member: String(row?.member || '').trim() === '—' ? '' : String(row?.member || '').trim(),
    item: itemValue === '—' ? '' : itemValue,
    duration,
    startDate,
    endDate,
    date: saleDate || startDate || '',
    amount: (() => {
      if (membershipLike) {
        if (Number.isFinite(packageFee)) return String(Math.max(0, packageFee));
        // Legacy rows: subtract known registration from total so package fee stays editable
        return Number.isFinite(total)
          ? String(Math.max(0, total - registrationFee))
          : '';
      }
      return Number.isFinite(total) ? String(total) : '';
    })(),
    registrationFee: membershipLike ? String(registrationFee) : '0',
    paymentSplits,
    note: userNoteOnly(row?.paymentNote),
  };
}

/**
 * Edit modal for ancillary / guest / renewal / payments sale rows
 * (same pattern as Memberships edit).
 */
export default function SaleEditModal({
  row,
  onClose,
  onSaved,
  title = 'Edit Sale',
  itemLabel = 'Item / Package',
  itemPlaceholder = 'Package or item name',
  saleLabel = '',
  itemIsTrainer = false,
  trainerOptions = [],
  durationOptions,
}) {
  const toast = usePanelToast();
  const options = durationOptions || durationOptionsForSource(row?.source);
  const trainerMode = itemIsTrainer || isTrainerSaleSource(row?.source);
  const membershipLike = isMembershipLikeSource(row?.source);
  const [form, setForm] = useState(() => buildInitialForm(row, itemLabel));
  const [saving, setSaving] = useState(false);
  const [memberQuery, setMemberQuery] = useState(() => {
    const initial = buildInitialForm(row, itemLabel);
    return initial.gymId || initial.member || '';
  });
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberHits, setMemberHits] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const q = String(memberQuery || '').trim();
    if (!q) {
      setMemberHits(selectedMember ? [selectedMember] : []);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const result = await fetchMembersPage({ search: q, pageSize: 25 });
        if (!cancelled) setMemberHits(dedupeMembers(result.rows || []));
      } catch {
        if (!cancelled) setMemberHits([]);
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [memberQuery, selectedMember]);

  const handleMemberSelect = ({ name, gymId, member }) => {
    setSelectedMember(member || null);
    setForm((prev) => ({
      ...prev,
      member: name != null ? name : prev.member,
      gymId: gymId != null ? gymId : prev.gymId,
    }));
    if (member) {
      const serial = getGymMemberId(member) || gymId || '';
      setMemberQuery(serial || name || '');
    }
  };

  const paidPreview = useMemo(
    () => sumPaymentSplits(form.paymentSplits),
    [form.paymentSplits],
  );

  const totalDuePreview = useMemo(() => {
    const packageFee = Math.max(0, Number(form.amount) || 0);
    if (!membershipLike) return packageFee;
    const reg = Math.max(0, Number(form.registrationFee) || 0);
    const discount = Math.max(0, Number(row?.discountAmount) || 0);
    const pt = Math.max(0, Number(row?.personalTrainingFee || row?.personalTraining) || 0);
    const classes = Math.max(0, Number(row?.classesFee) || 0);
    return Math.max(0, packageFee - discount + reg + pt + classes);
  }, [form.amount, form.registrationFee, membershipLike, row]);

  const handlePeriodChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => syncSalePeriodFields(prev, { [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const paymentDbId = row?.rawId || row?.id;
    if (!paymentDbId) {
      toast.showError('Payment record not found.');
      return;
    }
    if (!String(form.item || '').trim()) {
      toast.showError(`Enter ${itemLabel.toLowerCase()}.`);
      return;
    }
    if (!String(form.member || '').trim()) {
      toast.showError('Enter member name.');
      return;
    }
    const amountBlank = String(form.amount ?? '').trim() === '';
    let packageFee = amountBlank ? 0 : Math.max(0, Number(form.amount) || 0);
    const registrationFee = membershipLike
      ? Math.max(0, Number(form.registrationFee) || 0)
      : Math.max(0, Number(row?.registrationFee) || 0);
    const discount = Math.max(0, Number(row?.discountAmount) || 0);
    const pt = Math.max(0, Number(row?.personalTrainingFee || row?.personalTraining) || 0);
    const classes = Math.max(0, Number(row?.classesFee) || 0);
    let totalDue = membershipLike
      ? Math.max(0, packageFee - discount + registrationFee + pt + classes)
      : packageFee;
    const paid = sumPaymentSplits(form.paymentSplits);
    if (paid < 0) {
      toast.showError('Enter a valid paid amount.');
      return;
    }
    if (amountBlank && paid > 0 && !membershipLike) {
      packageFee = paid;
      totalDue = paid;
    } else if (paid > totalDue) {
      toast.showError('Total paid cannot exceed the sale total.');
      return;
    }

    try {
      setSaving(true);
      const durationLabel = formatDurationLabel(form.duration, options);
      const trainerOrItem = stripSaleTypePrefix(String(form.item || '').trim())
        || String(form.item || '').trim();
      const tag = String(saleLabel || '').trim();
      const packageName = trainerMode && tag && trainerOrItem
        && !new RegExp(`^${tag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i').test(trainerOrItem)
        ? `${tag} — ${trainerOrItem}`
        : cleanSalePackageName(trainerOrItem, trainerOrItem);
      await updatePaymentSale(paymentDbId, {
        memberName: form.member,
        gymMemberId: form.gymId,
        packageName,
        duration: durationLabel,
        startDate: form.startDate,
        endDate: form.endDate,
        date: form.date,
        packageFee,
        registrationFee: membershipLike ? registrationFee : undefined,
        paymentSplits: form.paymentSplits,
        paymentNote: form.note,
      });
      notifyPaymentsUpdated();
      toast.showUpdated(title.replace(/^Edit\s+/i, '') || 'Sale');
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.showError(err.message || 'Failed to update sale');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="subscription-modal-overlay" onClick={onClose}>
      <div
        className="subscription-modal admin-form-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sale-edit-title"
      >
        <div className="subscription-modal-header">
          <h2 id="sale-edit-title" className="subscription-modal-title">{title}</h2>
          <button type="button" className="subscription-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <form className="subscription-form" onSubmit={handleSubmit}>
          <div className="membership-form-grid">
            <div className="owner-form-group membership-form-full">
              <GymIdMemberSearch
                id="sale-edit-member-search"
                label="Serial No / Member"
                query={memberQuery}
                onQueryChange={setMemberQuery}
                selectedName={form.member}
                selectedGymId={form.gymId}
                selectedMember={selectedMember}
                onSelect={handleMemberSelect}
                members={memberHits}
                serverFiltered
                editableSelection
                placeholder="Type serial (e.g. 2) to see related members"
              />
            </div>
            <div className="owner-form-group membership-form-full">
              <label className="owner-form-label" htmlFor="sale-edit-item">{itemLabel}</label>
              <input
                id="sale-edit-item"
                type="text"
                className="owner-form-input"
                value={form.item}
                onChange={(e) => setForm((p) => ({ ...p, item: e.target.value }))}
                placeholder={itemPlaceholder}
                list={trainerMode ? 'sale-edit-trainer-list' : undefined}
                autoComplete={trainerMode ? 'off' : undefined}
                required
              />
              {trainerMode ? (
                <datalist id="sale-edit-trainer-list">
                  {(trainerOptions || []).map((name) => (
                    <option key={name} value={name} />
                  ))}
                </datalist>
              ) : null}
            </div>

            <SaleDurationField
              idPrefix="sale-edit"
              duration={form.duration}
              startDate={form.startDate}
              endDate={form.endDate}
              onPeriodChange={handlePeriodChange}
              durationOptions={options}
            />

            <div className="owner-form-group">
              <label className="owner-form-label" htmlFor="sale-edit-date">Sale Date</label>
              <input
                id="sale-edit-date"
                type="date"
                className="owner-form-input"
                value={form.date || ''}
                onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
              />
            </div>

            {membershipLike ? (
              <>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="sale-edit-package-fee">Package Fee</label>
                  <input
                    id="sale-edit-package-fee"
                    type="text"
                    inputMode="decimal"
                    className="owner-form-input"
                    value={form.amount == null ? '' : String(form.amount)}
                    onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))}
                    placeholder="Optional — leave blank (shows as 0)"
                  />
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="sale-edit-registration-fee">Registration Fee</label>
                  <input
                    id="sale-edit-registration-fee"
                    type="text"
                    inputMode="decimal"
                    className="owner-form-input"
                    value={form.registrationFee == null ? '' : String(form.registrationFee)}
                    onChange={(e) => setForm((p) => ({ ...p, registrationFee: e.target.value }))}
                    placeholder="0"
                  />
                </div>
                <SaleAmountFields
                  idPrefix="sale-edit"
                  totalAmount={String(totalDuePreview)}
                  paymentSplits={form.paymentSplits}
                  onTotalChange={() => {}}
                  onPaymentSplitsChange={(paymentSplits) => setForm((p) => ({ ...p, paymentSplits }))}
                  totalReadOnly
                />
              </>
            ) : (
              <SaleAmountFields
                idPrefix="sale-edit"
                totalAmount={form.amount}
                paymentSplits={form.paymentSplits}
                onTotalChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))}
                onPaymentSplitsChange={(paymentSplits) => setForm((p) => ({ ...p, paymentSplits }))}
              />
            )}

            <div className="owner-form-group membership-form-full">
              <label className="owner-form-label" htmlFor="sale-edit-note">Note</label>
              <input
                id="sale-edit-note"
                type="text"
                className="owner-form-input"
                value={form.note}
                onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))}
                placeholder="Optional note"
              />
              <p className="owner-placeholder-text rec-sale-payment-hint" style={{ marginTop: 8 }}>
                {membershipLike ? (
                  <>
                    Total due: <strong>{formatCurrency(totalDuePreview)}</strong>
                    {' · '}
                  </>
                ) : null}
                Amount paid: <strong>{formatCurrency(paidPreview)}</strong>
              </p>
            </div>
          </div>

          <div className="subscription-form-actions">
            <button type="submit" className="owner-btn owner-btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
            <button type="button" className="owner-btn owner-btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
